
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';
import { useReferralStats } from '@/hooks/affiliates/useReferralStats';
import { usePermissions } from '@/contexts/unified-permissions';

export const ReferralDashboard = () => {
  const { user } = usePermissions();
  const { stats, loading, error, statusMessage } = useReferralStats(user?.id);

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Loading skeleton for main content */}
        <div className="space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
          <div className="grid gap-4 md:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-red-500">Error loading referral dashboard: {error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Status Message for Offline/Cache */}
      {statusMessage && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-blue-600 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {statusMessage}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Header Section */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Unlimited Referral Rewards</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Share The Mat Monster with your BJJ community and earn unlimited free premium months
        </p>
      </div>

      {/* How it Works Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            How it Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
              <p className="text-sm">Share your unique referral link with friends</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
              <p className="text-sm">Your friends get 30% off their first 3 months of premium</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
              <p className="text-sm">For every 3 friends who convert to premium, you earn 1 month of free premium</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
              <p className="text-sm">There's no limit to how many rewards you can earn!</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Reward Stats Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Your Reward Stats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats?.totalReferrals || 0}</div>
              <p className="text-sm text-muted-foreground">Referral Signups</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{stats?.premiumConversions || 0}</div>
              <p className="text-sm text-muted-foreground">Total Premium Conversions</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{stats?.progressToNextReward || 0}/3</div>
              <p className="text-sm text-muted-foreground">Progress to Free Reward</p>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Your Rewards History Section */}
      <Card>
        <CardHeader>
          <CardTitle>Your Rewards History</CardTitle>
          <p className="text-sm text-muted-foreground">Complete history of rewards you've earned from your referrals</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Rewards Yet</h3>
            <p className="text-muted-foreground mb-4">
              Refer 3 friends who sign up for premium and you'll get 1 month of premium access for free! Keep referring to earn more free months with no limit.
            </p>
            <p className="text-sm text-muted-foreground">
              Check out the Sharing Tools tab to find your unique referral link and start sharing
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
